import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../services/apiClient';

/**
 * useBusquedaRemota — selector que busca en el servidor, no en memoria.
 *
 * El problema que resuelve
 * -----------------------
 * Para que un buscador encontrara CUALQUIER registro, el sistema bajaba el
 * catálogo entero al navegador y filtraba ahí. Con `page_size=10000` eso
 * funciona… hasta que el catálogo crece: abrir una orden de trabajo llegó a
 * costar 890 KB sólo de tareas, y un tenant con 10.000 órdenes vuelve a chocar
 * contra el techo — esta vez sin solución, porque el número ya no se puede
 * seguir subiendo.
 *
 * Acá la búsqueda la hace Postgres: se pide una página chica filtrada por
 * `search`, y el usuario alcanza TODAS las filas de la tabla sin que ninguna
 * pantalla tenga que tenerlas en memoria. Es lo que hace que se pueda bajar el
 * `page_size` sin volver al problema original de "sólo veo 25".
 *
 * Reglas que respeta (aprendidas de los buscadores que ya funcionaban bien):
 *  - El registro YA seleccionado se conserva en la lista aunque no venga en los
 *    resultados de la búsqueda nueva. Si no, el campo se vería vacío al tipear.
 *  - Un fallo de red NO se traga: `error` queda en true para que el campo lo
 *    muestre. Una búsqueda que falla en silencio hace que el usuario crea que el
 *    registro no existe y lo cargue duplicado.
 *  - Las respuestas fuera de orden se descartan: si el usuario tipea rápido, la
 *    respuesta de un término viejo no puede pisar la del término actual.
 *
 * @param {Object} opciones
 * @param {string} opciones.endpoint            Ruta relativa al apiClient, ej. 'tareas/'.
 * @param {Object} [opciones.params]            Filtros fijos que van en cada pedido.
 * @param {number} [opciones.pageSize=50]       Tamaño de cada tanda.
 * @param {number} [opciones.debounceMs=300]    Espera antes de pegarle al servidor.
 * @param {Function} [opciones.idSeleccionado]  Devuelve el id que hay que preservar.
 * @param {boolean} [opciones.habilitado=true]  Permite diferir la primera carga.
 */
export default function useBusquedaRemota({
  endpoint,
  params = undefined,
  pageSize = 50,
  debounceMs = 300,
  idSeleccionado = undefined,
  habilitado = true,
}) {
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [error, setError] = useState(false);

  const debounceRef = useRef(null);
  const terminoRef = useRef('');
  const paginaRef = useRef(1);
  // Cada búsqueda lleva número: sólo la última puede escribir el estado.
  const corridaRef = useRef(0);

  const paramsKey = JSON.stringify(params || {});
  const idSeleccionadoRef = useRef(idSeleccionado);
  idSeleccionadoRef.current = idSeleccionado;

  const pedir = useCallback(async (termino, pagina) => {
    const fijos = JSON.parse(paramsKey);
    const resp = await apiClient.get(endpoint, {
      params: {
        ...fijos,
        ...(termino ? { search: termino } : {}),
        page: pagina,
        page_size: pageSize,
      },
    });
    const data = resp.data;
    return {
      filas: Array.isArray(data) ? data : (data?.results || []),
      hayMas: Array.isArray(data) ? false : Boolean(data?.next),
    };
  }, [endpoint, paramsKey, pageSize]);

  const buscar = useCallback((terminoCrudo) => {
    if (!habilitado) return;
    const termino = String(terminoCrudo || '').trim();
    clearTimeout(debounceRef.current);

    const correr = async () => {
      const corrida = ++corridaRef.current;
      terminoRef.current = termino;
      paginaRef.current = 1;
      setBuscando(true);
      try {
        const { filas, hayMas } = await pedir(termino, 1);
        if (corrida !== corridaRef.current) return; // llegó tarde: ya hay otra búsqueda
        setError(false);
        setHasMore(hayMas);
        setItems((previos) => {
          const selId = String(idSeleccionadoRef.current ?? '');
          if (!selId || filas.some((f) => String(f.id) === selId)) return filas;
          // El seleccionado no vino en esta búsqueda: se mantiene al frente para
          // que el campo siga mostrando su etiqueta.
          const sel = previos.find((f) => String(f.id) === selId);
          return sel ? [sel, ...filas] : filas;
        });
      } catch {
        if (corrida !== corridaRef.current) return;
        setError(true);
        setHasMore(false);
      } finally {
        if (corrida === corridaRef.current) setBuscando(false);
      }
    };

    // Sin término (apertura del desplegable) no se hace esperar al usuario.
    if (termino && debounceMs > 0) {
      debounceRef.current = setTimeout(correr, debounceMs);
    } else {
      correr();
    }
  }, [pedir, debounceMs, habilitado]);

  const cargarMas = useCallback(async () => {
    if (!hasMore || loadingMore) return;
    const siguiente = paginaRef.current + 1;
    const corrida = corridaRef.current;
    setLoadingMore(true);
    try {
      const { filas, hayMas } = await pedir(terminoRef.current, siguiente);
      if (corrida !== corridaRef.current) return; // cambió la búsqueda mientras tanto
      paginaRef.current = siguiente;
      setHasMore(hayMas);
      setItems((previos) => {
        const vistos = new Set(previos.map((f) => String(f.id)));
        return [...previos, ...filas.filter((f) => !vistos.has(String(f.id)))];
      });
      setError(false);
    } catch {
      if (corrida === corridaRef.current) setError(true);
    } finally {
      setLoadingMore(false);
    }
  }, [pedir, hasMore, loadingMore]);

  /** Suma un registro a la lista (p. ej. el que se acaba de crear en un modal). */
  const sumarItem = useCallback((item) => {
    if (!item?.id) return;
    setItems((previos) => (
      previos.some((f) => String(f.id) === String(item.id)) ? previos : [item, ...previos]
    ));
  }, []);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  return { items, buscar, cargarMas, sumarItem, hasMore, loadingMore, buscando, error };
}
